import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, AlertTriangle, CheckCircle, XCircle, Search, 
  Globe, Briefcase, Phone, Mail, Calendar, ChevronRight, 
  ArrowRight, ExternalLink, Flag, FileText, AlertOctagon, 
  TrendingUp, Users, Clock, Eye, Zap, HelpCircle, ChevronDown, 
  ChevronUp, Activity, Database, Star, MessageSquare, Lock,
  Smartphone, Bitcoin, User, AlertCircle, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ==================== URL PATTERN TYPES ====================
const PAGE_TYPES = {
  IS_LEGIT: 'is_legit',      // /is-[company]-legit
  REVIEWS: 'reviews',         // /reviews/[company]
  DOMAIN_CHECK: 'domain',     // /check/domain/[domain]
  VERIFY: 'verify',           // /verify/[entity]
  GENERIC: 'generic'          // fallback
};

// ==================== SAMPLE DATA FOR TESTING ====================
// This sample data will be used for demo pages and can be connected to a real database later
const SAMPLE_ENTITIES = {
  // Companies
  'global-crypto-traders': {
    name: 'Global Crypto Traders',
    type: 'company',
    riskScore: 78,
    riskLevel: 'High Risk',
    reportCount: 23,
    firstSeen: '2024-09-15',
    lastScanned: '2025-01-10',
    description: 'Alleged investment platform with multiple fraud indicators.',
    redFlags: [
      'No verifiable company registration',
      'Pressure tactics reported',
      'Unrealistic return promises (300% monthly)',
      'No physical address listed',
      'New domain registered recently'
    ]
  },
  'fast-finance-ltd': {
    name: 'Fast Finance Ltd',
    type: 'company',
    riskScore: 65,
    riskLevel: 'Moderate Risk',
    reportCount: 8,
    firstSeen: '2024-11-20',
    lastScanned: '2025-01-08',
    description: 'Financial services company with some concerning patterns.',
    redFlags: [
      'Limited online presence',
      'Inconsistent contact information',
      'Vague service descriptions',
      'No clear refund policy'
    ]
  },
  'trusted-investments-uk': {
    name: 'Trusted Investments UK',
    type: 'company',
    riskScore: 45,
    riskLevel: 'Medium Risk',
    reportCount: 3,
    firstSeen: '2024-08-10',
    lastScanned: '2025-01-05',
    description: 'Investment firm requiring additional verification.',
    redFlags: [
      'Limited company history',
      'Standard verification recommended'
    ]
  },
  'example-shopping': {
    name: 'Example Shopping',
    type: 'company',
    riskScore: 22,
    riskLevel: 'Low Risk',
    reportCount: 1,
    firstSeen: '2023-06-15',
    lastScanned: '2025-01-09',
    description: 'E-commerce platform with minimal fraud indicators.',
    redFlags: [
      'Standard verification recommended'
    ]
  },
  // Domains
  'crypto-profits-now-com': {
    name: 'crypto-profits-now.com',
    type: 'domain',
    riskScore: 92,
    riskLevel: 'Critical Risk',
    reportCount: 45,
    firstSeen: '2024-10-01',
    lastScanned: '2025-01-10',
    description: 'Cryptocurrency investment website with multiple scam reports.',
    redFlags: [
      'Domain registered 3 months ago',
      'Anonymous domain registration (WHOIS protected)',
      'No valid SSL certificate',
      'Promises guaranteed 500% returns',
      'Multiple user scam reports',
      'Hosted on high-risk infrastructure'
    ]
  },
  'secure-bank-verification-uk': {
    name: 'secure-bank-verification.uk',
    type: 'domain',
    riskScore: 88,
    riskLevel: 'High Risk',
    reportCount: 31,
    firstSeen: '2024-12-05',
    lastScanned: '2025-01-09',
    description: 'Suspected phishing website impersonating banking services.',
    redFlags: [
      'Impersonates banking institutions',
      'Recently registered domain',
      'Requests sensitive login credentials',
      'No verifiable business entity',
      'Reported as phishing site'
    ]
  },
  'legit-marketplace-co-uk': {
    name: 'legit-marketplace.co.uk',
    type: 'domain',
    riskScore: 35,
    riskLevel: 'Low Risk',
    reportCount: 2,
    firstSeen: '2022-03-10',
    lastScanned: '2025-01-08',
    description: 'E-commerce marketplace with established history.',
    redFlags: [
      'Standard caution recommended for all online purchases'
    ]
  },
  // Additional test entries
  'quick-loan-approvals': {
    name: 'Quick Loan Approvals',
    type: 'company',
    riskScore: 71,
    riskLevel: 'High Risk',
    reportCount: 18,
    firstSeen: '2024-07-22',
    lastScanned: '2025-01-07',
    description: 'Loan company with advance fee fraud indicators.',
    redFlags: [
      'Requests upfront fees before loan approval',
      'No FCA authorization found',
      'Uses pressure tactics',
      'Unverifiable company address'
    ]
  },
  'forex-masters-academy': {
    name: 'Forex Masters Academy',
    type: 'company',
    riskScore: 67,
    riskLevel: 'Moderate Risk',
    reportCount: 12,
    firstSeen: '2024-06-15',
    lastScanned: '2025-01-06',
    description: 'Trading education platform with mixed reviews.',
    redFlags: [
      'Unrealistic income claims',
      'High-pressure sales tactics',
      'Limited refund options'
    ]
  }
};

// ==================== UTILITY FUNCTIONS ====================

// Generate a slug from entity name
const toSlug = (str) => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 100);
};

// Normalize entity from URL slug to display name
const normalizeEntity = (slug) => {
  if (!slug) return '';
  return slug
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Convert slug to domain format for domain checks
const slugToDomain = (slug) => {
  if (!slug) return '';
  let domain = slug.toLowerCase().replace(/\s+/g, '-');
  domain = domain
    .replace(/-co-uk$/, '.co.uk')
    .replace(/-org-uk$/, '.org.uk')
    .replace(/-com$/, '.com')
    .replace(/-uk$/, '.uk')
    .replace(/-org$/, '.org')
    .replace(/-net$/, '.net')
    .replace(/-io$/, '.io');
  return domain;
};

// Generate deterministic risk score from entity string (fallback)
const generateFallbackRiskScore = (entity) => {
  let hash = 0;
  const str = entity.toLowerCase();
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash % 60) + 25;
};

// Trusted entities whitelist
const TRUSTED_ENTITIES = [
  'actionfraudone', 'legal-ac', 'legal alliance', 'google', 'amazon', 'microsoft', 
  'apple', 'bbc', 'gov uk', 'nhs', 'action fraud', 'fca', 'hmrc',
  'paypal', 'ebay', 'facebook', 'meta', 'twitter', 'linkedin'
];

const isTrustedEntity = (entity) => {
  const normalized = entity.toLowerCase();
  return TRUSTED_ENTITIES.some(trusted => 
    normalized.includes(trusted) || trusted.includes(normalized.replace(/\s+/g, '-'))
  );
};

// Get risk assessment data
const getRiskAssessment = (score, entity) => {
  if (isTrustedEntity(entity)) {
    return {
      level: 'Trusted',
      verdict: 'Verified Legitimate Entity',
      color: 'green',
      icon: CheckCircle,
      description: 'This entity has been verified as a legitimate, established organization.',
      badge: 'VERIFIED',
      action: 'This entity is generally considered safe, but always verify directly.'
    };
  }
  
  if (score >= 80) {
    return {
      level: 'Critical Risk',
      verdict: 'Likely Fraudulent',
      color: 'red',
      icon: XCircle,
      description: 'Strong fraud indicators present. Do not engage or send money.',
      badge: 'DANGER',
      action: 'We strongly advise against any engagement. If you have been contacted, do not respond.'
    };
  }
  if (score >= 60) {
    return {
      level: 'High Risk',
      verdict: 'Significant Concerns',
      color: 'orange',
      icon: AlertOctagon,
      description: 'Multiple fraud indicators detected. Avoid sharing personal information.',
      badge: 'HIGH RISK',
      action: 'Exercise extreme caution. Verify through independent official channels before any engagement.'
    };
  }
  if (score >= 40) {
    return {
      level: 'Medium Risk',
      verdict: 'Exercise Caution',
      color: 'yellow',
      icon: AlertTriangle,
      description: 'Some patterns warrant additional verification before engagement.',
      badge: 'CAUTION',
      action: 'We recommend additional verification before proceeding with any financial transactions.'
    };
  }
  return {
    level: 'Low Risk',
    verdict: 'Likely Legitimate',
    color: 'green',
    icon: CheckCircle,
    description: 'No significant fraud indicators detected. Standard caution recommended.',
    badge: 'LOW RISK',
    action: 'While no major red flags were found, always maintain standard caution for online interactions.'
  };
};

// Generate warning signs
const generateWarningSigns = (entityData, score) => {
  if (entityData?.redFlags && entityData.redFlags.length > 0) {
    return entityData.redFlags;
  }
  
  const highRiskWarnings = [
    'Recently registered domain or company',
    'No verifiable business address',
    'Pressure tactics or urgency messaging',
    'Requests for unusual payment methods (crypto, gift cards)',
    'Poor or no legitimate online presence',
    'Grammatical errors in official communications',
    'Promises unrealistic returns or guarantees'
  ];
  
  const mediumRiskWarnings = [
    'Limited company history available',
    'Inconsistent contact information',
    'Vague service descriptions',
    'No clear refund or cancellation policy',
    'Limited customer reviews available'
  ];
  
  const lowRiskWarnings = [
    'Standard verification recommended',
    'Check for recent reviews before engaging'
  ];

  if (score >= 60) return highRiskWarnings.slice(0, 5);
  if (score >= 40) return mediumRiskWarnings;
  return lowRiskWarnings;
};

// Generate FAQs dynamically
const generateFAQs = (entity, entityType, verdict, reportCount) => {
  return [
    {
      q: `Is ${entity} legitimate?`,
      a: `Based on our fraud intelligence analysis, ${entity} has been assessed as "${verdict}". ${reportCount > 0 ? `There have been ${reportCount} user reports submitted about this entity.` : ''} We recommend conducting additional verification before engaging in any financial transactions or sharing personal information.`
    },
    {
      q: `Is ${entity} a scam?`,
      a: `Our automated analysis has identified certain risk factors for ${entity}. While we cannot definitively label any entity as a "scam" without investigation, our risk assessment provides guidance. For high-value decisions, consider our Professional Investigation service for manual verification.`
    },
    {
      q: `How can I verify if ${entity} is safe?`,
      a: `You can verify ${entity} by: 1) Checking official company registration records (Companies House for UK entities), 2) Searching for verified reviews on trusted platforms, 3) Contacting them directly through official channels, 4) Running a detailed scam check through our fraud intelligence service.`
    },
    {
      q: `What should I do if I've been scammed by ${entity}?`,
      a: `If you believe you've been scammed: 1) Stop all communication immediately, 2) Contact your bank if you've sent money, 3) Report to Action Fraud (UK: 0300 123 2040) or your local authorities, 4) Document all evidence, 5) Submit a report on our platform to help warn others.`
    },
    {
      q: `Are there complaints about ${entity}?`,
      a: `Our fraud intelligence database monitors for reports and complaints. ${reportCount > 0 ? `We have received ${reportCount} reports related to ${entity}.` : `We have not received specific user reports about ${entity} yet.`} Run a full scam check to see the latest risk assessment.`
    }
  ];
};

// Generate related internal links
const generateInternalLinks = async (entity, entityType, relatedScams = []) => {
  const commonScams = [
    { name: 'Romance Scams', slug: 'romance-scams' },
    { name: 'Crypto Scams', slug: 'crypto-investment-scams' },
    { name: 'Job Offer Scams', slug: 'job-offer-scams' },
    { name: 'Phishing Attacks', slug: 'phishing-websites' },
    { name: 'Investment Fraud', slug: 'investment-fraud' },
    { name: 'Advance Fee Fraud', slug: 'advance-fee-fraud' }
  ];
  
  // Try to fetch related entities from database
  let relatedEntities = [];
  try {
    const response = await fetch(`${API_URL}/api/seo/entities/list?limit=6&type=${entityType}`);
    if (response.ok) {
      const data = await response.json();
      relatedEntities = data.entities
        .filter(e => e.slug !== toSlug(entity))
        .slice(0, 4)
        .map(e => ({
          name: e.name,
          slug: e.slug
        }));
    }
  } catch (error) {
    console.log('Using sample entities for related links');
  }
  
  // Fallback to sample entities if no database entities
  if (relatedEntities.length === 0) {
    relatedEntities = Object.keys(SAMPLE_ENTITIES)
      .filter(slug => slug !== toSlug(entity))
      .slice(0, 4)
      .map(slug => ({
        name: SAMPLE_ENTITIES[slug].name,
        slug: slug
      }));
  }
  
  return { scamCategories: commonScams, relatedEntities };
};

// ==================== MAIN COMPONENT ====================

const ScalableEntityPage = () => {
  const params = useParams();
  const location = useLocation();
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityData, setEntityData] = useState(null);
  const [reportCount, setReportCount] = useState(0);
  const [relatedLinks, setRelatedLinks] = useState({ scamCategories: [], relatedEntities: [] });
  
  // Determine page type and extract entity from URL
  let pageType = PAGE_TYPES.GENERIC;
  let entitySlug = '';
  let entityType = 'company';
  
  const path = location.pathname;
  
  // Pattern: /is-[company]-legit
  if (path.match(/^\/is-.+-legit$/)) {
    pageType = PAGE_TYPES.IS_LEGIT;
    const match = path.match(/^\/is-(.+)-legit$/);
    if (match) entitySlug = match[1];
  }
  // Pattern: /reviews/[company]
  else if (path.startsWith('/reviews/')) {
    pageType = PAGE_TYPES.REVIEWS;
    entitySlug = params.entity || params.company || '';
  }
  // Pattern: /check/domain/[domain]
  else if (path.startsWith('/check/domain/')) {
    pageType = PAGE_TYPES.DOMAIN_CHECK;
    entitySlug = params.domain || params.entity || '';
    entityType = 'domain';
  }
  // Pattern: /verify/[entity]
  else if (path.startsWith('/verify/')) {
    pageType = PAGE_TYPES.VERIFY;
    entitySlug = params.entity || '';
  }
  // Generic fallback
  else if (params.entity) {
    entitySlug = params.entity;
  }

  // Get entity display name
  const entityName = entityData?.name || 
    (entityType === 'domain' ? slugToDomain(entitySlug) : normalizeEntity(entitySlug));
  
  // Get or generate risk data
  const riskScore = entityData?.riskScore || generateFallbackRiskScore(entityName);
  const assessment = getRiskAssessment(riskScore, entityName);
  const warningSigns = generateWarningSigns(entityData, riskScore);
  const faqs = generateFAQs(entityName, entityType, assessment.verdict, reportCount);
  const { scamCategories, relatedEntities } = relatedLinks;
  
  const RiskIcon = assessment.icon;
  const currentDate = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  // Fetch entity data from backend or use sample data
  useEffect(() => {
    const fetchEntityData = async () => {
      setLoading(true);
      
      try {
        // First check sample data (local fallback)
        const sampleData = SAMPLE_ENTITIES[entitySlug];
        
        // Try to fetch from backend SEO endpoint first
        const response = await fetch(`${API_URL}/api/seo/entity/${entitySlug}`);
        if (response.ok) {
          const data = await response.json();
          if (data.entity) {
            const fetchedEntityType = data.entity.type || entityType;
            setEntityData({
              name: data.entity.name,
              type: fetchedEntityType,
              riskScore: data.entity.risk_score,
              riskLevel: data.entity.risk_level,
              reportCount: data.report_count || 0,
              firstSeen: data.entity.first_seen,
              lastScanned: data.entity.last_scanned,
              description: data.entity.recommended_action || data.entity.description,
              redFlags: data.entity.red_flags?.map(f => typeof f === 'string' ? f : f.description) || [],
              relatedScams: data.entity.related_scams || [],
              country: data.entity.country,
              industry: data.entity.industry,
              domain: data.entity.domain
            });
            setReportCount(data.report_count || 0);
            
            // Fetch related links
            const links = await generateInternalLinks(data.entity.name, fetchedEntityType, data.entity.related_scams);
            setRelatedLinks(links);
            
            setLoading(false);
            return;
          }
        }
        
        // Use local sample data as fallback
        if (sampleData) {
          setEntityData(sampleData);
          setReportCount(sampleData.reportCount || 0);
          const links = await generateInternalLinks(sampleData.name, sampleData.type);
          setRelatedLinks(links);
          setLoading(false);
          return;
        }
        
        // Try the older scam-check endpoint
        const legacyResponse = await fetch(`${API_URL}/api/scam-check/${entitySlug}`);
        if (legacyResponse.ok) {
          const data = await legacyResponse.json();
          if (data.entity) {
            setEntityData({
              name: data.entity.name,
              type: data.entity.type,
              riskScore: data.entity.risk_score,
              riskLevel: data.entity.risk_level,
              reportCount: data.report_count || 0,
              firstSeen: data.entity.first_seen,
              lastScanned: data.entity.last_scanned,
              description: data.entity.recommended_action,
              redFlags: data.entity.red_flags?.map(f => typeof f === 'string' ? f : f.description) || []
            });
            setReportCount(data.report_count || 0);
          }
        }
        
        // Load default related links if nothing found
        const defaultLinks = await generateInternalLinks(normalizeEntity(entitySlug), entityType);
        setRelatedLinks(defaultLinks);
        
      } catch (error) {
        console.log('Using generated data for entity:', entitySlug);
        // Load fallback related links
        const fallbackLinks = await generateInternalLinks(normalizeEntity(entitySlug), entityType);
        setRelatedLinks(fallbackLinks);
      }
      
      setLoading(false);
    };

    if (entitySlug) {
      fetchEntityData();
    } else {
      setLoading(false);
    }
  }, [entitySlug, entityType]);

  // Generate SEO metadata based on page type
  const getPageMeta = () => {
    const currentYear = new Date().getFullYear();
    
    switch (pageType) {
      case PAGE_TYPES.IS_LEGIT:
        return {
          title: `Is ${entityName} Legit? Scam Check & Fraud Review ${currentYear}`,
          description: `Is ${entityName} legitimate or a scam? Check our fraud intelligence analysis with risk score of ${riskScore}/100, warning signs, and ${reportCount} user reports. Find out if ${entityName} is safe.`,
          h1: `Is ${entityName} Legit?`,
          subtitle: `Fraud Intelligence Analysis & Scam Check`
        };
      case PAGE_TYPES.REVIEWS:
        return {
          title: `${entityName} Reviews - Scam Check & Fraud Report ${currentYear}`,
          description: `Read ${entityName} reviews and fraud analysis. Risk score: ${riskScore}/100 (${assessment.level}). Our scam checker provides detailed risk assessment and warning signs.`,
          h1: `${entityName} Reviews`,
          subtitle: `Scam Check & Fraud Analysis`
        };
      case PAGE_TYPES.DOMAIN_CHECK:
        return {
          title: `${entityName} - Website Scam Check & Domain Analysis ${currentYear}`,
          description: `Is ${entityName} a scam website? Check domain legitimacy with our fraud scanner. Risk score: ${riskScore}/100. Get safety analysis and scam indicators.`,
          h1: `${entityName}`,
          subtitle: `Website & Domain Scam Check`
        };
      case PAGE_TYPES.VERIFY:
        return {
          title: `Verify ${entityName} - Legitimacy Check & Fraud Analysis ${currentYear}`,
          description: `Verify if ${entityName} is legitimate. Risk score: ${riskScore}/100. Our fraud intelligence platform provides comprehensive entity verification.`,
          h1: `Verify ${entityName}`,
          subtitle: `Legitimacy Check & Risk Assessment`
        };
      default:
        return {
          title: `${entityName} Scam Check - Fraud Intelligence Analysis ${currentYear}`,
          description: `Check if ${entityName} is a scam. Risk score: ${riskScore}/100 (${assessment.level}). Our fraud intelligence platform provides risk analysis and warning signs.`,
          h1: `${entityName} Scam Check`,
          subtitle: `Fraud Intelligence Analysis`
        };
    }
  };

  const pageMeta = getPageMeta();

  // Color classes for risk levels
  const colorClasses = {
    green: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', badge: 'bg-green-500', ring: 'ring-green-500/50' },
    yellow: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', badge: 'bg-yellow-500', ring: 'ring-yellow-500/50' },
    orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500', ring: 'ring-orange-500/50' },
    red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500', ring: 'ring-red-500/50' }
  };
  const colors = colorClasses[assessment.color] || colorClasses.yellow;

  // Entity type icon
  const getEntityIcon = () => {
    const icons = {
      domain: Globe,
      company: Briefcase,
      phone: Phone,
      email: Mail,
      crypto_wallet: Bitcoin,
      social_handle: User
    };
    return icons[entityType] || Briefcase;
  };
  const EntityIcon = getEntityIcon();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-[#1e40af]/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-xl font-bold text-white mb-2">Analyzing Entity</h2>
          <p className="text-gray-400">Running fraud intelligence checks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628] pt-24 pb-16" data-testid="entity-seo-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageMeta.title}</title>
        <meta name="description" content={pageMeta.description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://actionfraudone.com${location.pathname}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageMeta.title} />
        <meta property="og:description" content={pageMeta.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://actionfraudone.com${location.pathname}`} />
        <meta property="og:site_name" content="ActionFraudOne" />
        <meta property="og:image" content="https://actionfraudone.com/actionfraudone-logo-transparent.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageMeta.title} />
        <meta name="twitter:description" content={pageMeta.description} />
        
        {/* Schema.org WebPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": pageMeta.title,
            "description": pageMeta.description,
            "url": `https://actionfraudone.com${location.pathname}`,
            "dateModified": new Date().toISOString(),
            "publisher": {
              "@type": "Organization",
              "name": "ActionFraudOne",
              "url": "https://actionfraudone.com"
            },
            "mainEntity": {
              "@type": "Review",
              "itemReviewed": {
                "@type": entityType === 'domain' ? "WebSite" : "Organization",
                "name": entityName
              },
              "reviewRating": {
                "@type": "Rating",
                "ratingValue": Math.max(1, 5 - Math.floor(riskScore / 25)),
                "bestRating": "5",
                "worstRating": "1",
                "ratingExplanation": `Risk Level: ${assessment.level} (Score: ${riskScore}/100)`
              },
              "author": {
                "@type": "Organization",
                "name": "ActionFraudOne"
              }
            }
          })}
        </script>

        {/* Schema.org FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.a
              }
            }))
          })}
        </script>

        {/* Schema.org BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://actionfraudone.com" },
              { "@type": "ListItem", "position": 2, "name": "Fraud Database", "item": "https://actionfraudone.com/scam-database" },
              { "@type": "ListItem", "position": 3, "name": entityName, "item": `https://actionfraudone.com${location.pathname}` }
            ]
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-testid="breadcrumbs">
          <Link to="/" className="hover:text-blue-400 transition-colors">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/scam-database" className="hover:text-blue-400 transition-colors">Fraud Database</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-blue-400 truncate max-w-[200px]">{entityName}</span>
        </nav>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* ==================== HERO SECTION WITH QUICK VERDICT ==================== */}
            <Card className={`${colors.bg} ${colors.border} border-2`} data-testid="verdict-card">
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-6">
                  <div className="flex-1">
                    <span className={`inline-block px-3 py-1 ${colors.badge} text-white text-xs font-bold rounded-full mb-3`} data-testid="risk-badge">
                      {assessment.badge}
                    </span>
                    <h1 className="text-2xl md:text-3xl font-bold text-white mb-2" data-testid="page-h1">
                      {pageMeta.h1}
                    </h1>
                    <p className="text-gray-400 text-sm flex items-center gap-2 flex-wrap">
                      <Clock className="w-4 h-4" />
                      Last updated: {currentDate}
                      <span className="text-gray-600">•</span>
                      <span>Fraud Intelligence Analysis</span>
                    </p>
                  </div>
                  <div className={`w-16 h-16 ${colors.bg} border ${colors.border} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <RiskIcon className={`w-8 h-8 ${colors.text}`} />
                  </div>
                </div>

                {/* Quick Verdict Box */}
                <div className="bg-[#0a1628]/70 rounded-xl p-5 mb-6 border border-[#1e40af]/10" data-testid="quick-verdict">
                  <div className="flex items-center gap-3 mb-3">
                    <Activity className={`w-5 h-5 ${colors.text}`} />
                    <span className="text-white font-semibold">Quick Verdict</span>
                  </div>
                  <p className={`text-xl font-bold ${colors.text} mb-2`}>{assessment.verdict}</p>
                  <p className="text-gray-400 text-sm">{assessment.description}</p>
                </div>

                {/* Fraud Risk Score */}
                <div className="space-y-4" data-testid="risk-score-section">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm font-medium">Fraud Risk Score</span>
                    <span className={`font-bold text-lg ${colors.text}`}>{riskScore}/100</span>
                  </div>
                  <div className="h-4 bg-[#0a1628] rounded-full overflow-hidden border border-[#1e40af]/20">
                    <div 
                      className={`h-full ${colors.badge} rounded-full transition-all duration-700`}
                      style={{ width: `${riskScore}%` }}
                    />
                  </div>
                  <p className="text-gray-500 text-xs">
                    Higher score = Higher risk. Score based on automated analysis of multiple indicators.
                  </p>
                </div>

                {/* Report Stats if available */}
                {reportCount > 0 && (
                  <div className="mt-6 flex items-center gap-6 pt-6 border-t border-[#1e40af]/20">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-red-400" />
                      <span className="text-white font-semibold">{reportCount}</span>
                      <span className="text-gray-400 text-sm">user reports</span>
                    </div>
                    {entityData?.firstSeen && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-blue-400" />
                        <span className="text-gray-400 text-sm">First seen: {entityData.firstSeen}</span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ==================== ENTITY DETAILS ==================== */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20" data-testid="entity-details">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <EntityIcon className="w-5 h-5 text-blue-400" />
                  Entity Details
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1e40af]/10">
                    <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Entity Name</p>
                    <p className="text-white font-medium break-all">{entityName}</p>
                  </div>
                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1e40af]/10">
                    <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Entity Type</p>
                    <p className="text-white font-medium capitalize">{entityType.replace('_', ' ')}</p>
                  </div>
                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1e40af]/10">
                    <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Risk Level</p>
                    <p className={`font-medium ${colors.text}`}>{assessment.level}</p>
                  </div>
                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1e40af]/10">
                    <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Analysis Date</p>
                    <p className="text-white font-medium">{currentDate}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ==================== WARNING SIGNS / RED FLAGS ==================== */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20" data-testid="warning-signs">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Warning Signs & Red Flags
                </h2>
                <div className="space-y-3">
                  {warningSigns.map((warning, index) => (
                    <div 
                      key={index} 
                      className={`flex items-start gap-3 p-4 ${colors.bg} border ${colors.border} rounded-lg`}
                    >
                      <AlertCircle className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
                      <span className="text-gray-300 text-sm">{warning}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* ==================== RECOMMENDED ACTION ==================== */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20" data-testid="recommended-action">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Info className="w-5 h-5 text-cyan-400" />
                  Recommended Action
                </h2>
                <div className={`p-4 ${colors.bg} border ${colors.border} rounded-lg`}>
                  <p className="text-gray-300 text-sm leading-relaxed">{assessment.action}</p>
                </div>
              </CardContent>
            </Card>

            {/* ==================== WHAT TO DO / SAFETY TIPS ==================== */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20" data-testid="safety-tips">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-cyan-400" />
                  How to Stay Safe
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-400 mb-2" />
                    <h3 className="text-white font-semibold text-sm mb-2">Do This</h3>
                    <ul className="text-gray-400 text-xs space-y-1.5">
                      <li>• Verify contact details independently</li>
                      <li>• Check company registration records</li>
                      <li>• Read multiple reviews from trusted sources</li>
                      <li>• Use secure payment methods with protection</li>
                      <li>• Research before sharing personal info</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-400 mb-2" />
                    <h3 className="text-white font-semibold text-sm mb-2">Avoid This</h3>
                    <ul className="text-gray-400 text-xs space-y-1.5">
                      <li>• Sending money via untraceable methods</li>
                      <li>• Sharing personal documents or IDs</li>
                      <li>• Acting under pressure or urgency</li>
                      <li>• Ignoring red flags or gut feelings</li>
                      <li>• Clicking links in unsolicited messages</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ==================== FAQ SECTION ==================== */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20" data-testid="faq-section">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-purple-400" />
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border border-[#1e40af]/20 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-[#1e40af]/5 transition-colors"
                        data-testid={`faq-question-${index}`}
                      >
                        <span className="text-white font-medium text-sm pr-4">{faq.q}</span>
                        {expandedFAQ === index ? (
                          <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {expandedFAQ === index && (
                        <div className="px-4 pb-4 text-gray-400 text-sm leading-relaxed" data-testid={`faq-answer-${index}`}>
                          {faq.a}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* ==================== RELATED SCAM CHECKS (Internal Links) ==================== */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20" data-testid="related-checks">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-400" />
                  Related Scam Checks
                </h2>
                
                {/* Related Entities from Database */}
                {relatedEntities.length > 0 && (
                  <div className="mb-4">
                    <p className="text-gray-500 text-xs uppercase tracking-wide mb-3">Recently Checked Entities</p>
                    <div className="grid grid-cols-2 gap-2">
                      {relatedEntities.map((item, index) => (
                        <Link
                          key={index}
                          to={`/is-${item.slug}-legit`}
                          className="p-3 bg-[#0a1628] border border-[#1e40af]/10 rounded-lg text-gray-400 text-sm hover:border-blue-400 hover:text-blue-400 transition-colors truncate"
                        >
                          Is {item.name} legit?
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Scam Category Links */}
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-3">Browse Scam Categories</p>
                <div className="flex flex-wrap gap-2">
                  {scamCategories.map((cat, index) => (
                    <Link
                      key={index}
                      to={`/scams/${cat.slug}`}
                      className="px-4 py-2 bg-[#0a1628] border border-[#1e40af]/10 rounded-lg text-gray-400 text-sm hover:border-blue-400 hover:text-blue-400 transition-colors"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ==================== SIDEBAR - 1 column ==================== */}
          <div className="space-y-6">
            
            {/* Primary CTA - Run Full Paid Check */}
            <Card className="bg-gradient-to-b from-[#1e40af]/30 to-[#1e40af]/10 border-[#1e40af]/40 border-2" data-testid="cta-paid-check">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Get Full Analysis</h3>
                <p className="text-gray-400 text-sm mb-6">
                  Run a comprehensive fraud intelligence scan on {entityName} with detailed risk indicators and evidence.
                </p>
                <Link to={`/scam-check/unlock?type=${entityType}&entity=${encodeURIComponent(entitySlug)}&query=${encodeURIComponent(entityName)}`}>
                  <Button className="w-full bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold py-6 text-lg" data-testid="cta-button-basic">
                    Run Scam Check - £4.99
                  </Button>
                </Link>
                <p className="text-gray-500 text-xs mt-3">Instant results • Detailed report</p>
                
                {/* Upgrade option */}
                <div className="mt-4 pt-4 border-t border-[#1e40af]/20">
                  <Link to={`/scam-check/unlock?type=${entityType}&entity=${encodeURIComponent(entitySlug)}&query=${encodeURIComponent(entityName)}&tier=pro`}>
                    <Button variant="outline" className="w-full border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" data-testid="cta-button-pro">
                      Advanced Analysis - £9.99
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Investigation Services Promo */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20" data-testid="investigation-promo">
              <CardContent className="p-6">
                <FileText className="w-10 h-10 text-purple-400 mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Need Deeper Analysis?</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Our Professional Investigation includes manual OSINT review, evidence documentation, and expert analysis.
                </p>
                <Link to="/investigations">
                  <Button variant="outline" className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                    View Investigation Services
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <p className="text-gray-500 text-xs mt-3 text-center">From £49</p>
              </CardContent>
            </Card>

            {/* Report This Entity */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20" data-testid="report-entity">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-2">Have Information?</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Help protect others by reporting any scam activity related to {entityName}.
                </p>
                <Link to={`/report-a-scam?entity=${encodeURIComponent(entityName)}&type=${entityType}`}>
                  <Button variant="outline" className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10">
                    <Flag className="w-4 h-4 mr-2" />
                    Report This Entity
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Scam Categories */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20" data-testid="scam-categories-sidebar">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-4">Scam Categories</h3>
                <div className="space-y-2">
                  {scamCategories.slice(0, 4).map((cat, index) => (
                    <Link
                      key={index}
                      to={`/scams/${cat.slug}`}
                      className="block p-3 bg-[#0a1628] rounded-lg text-gray-400 text-sm hover:bg-[#1e40af]/10 hover:text-blue-400 transition-colors"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
                <Link to="/scam-database" className="block mt-4 text-blue-400 text-sm hover:underline">
                  View all categories →
                </Link>
              </CardContent>
            </Card>

            {/* Check Another Entity */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20" data-testid="check-another">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-3">Check Another Entity</h3>
                <Link to="/">
                  <Button variant="outline" className="w-full border-[#1e40af]/50 text-blue-400 hover:bg-[#1e40af]/10">
                    <Search className="w-4 h-4 mr-2" />
                    New Scam Check
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Trust Badge */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/20">
              <CardContent className="p-6 text-center">
                <Shield className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                <p className="text-white font-semibold text-sm mb-1">Fraud Intelligence Platform</p>
                <p className="text-gray-500 text-xs">
                  Powered by ActionFraudOne
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ==================== INTERNAL LINKS SECTION (SEO) ==================== */}
        <div className="mt-12 pt-8 border-t border-[#1e40af]/20" data-testid="internal-links-section">
          <h2 className="text-xl font-bold text-white mb-6">More Scam Checks</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {Object.entries(SAMPLE_ENTITIES).slice(0, 6).map(([slug, data]) => (
              <Link
                key={slug}
                to={`/is-${slug}-legit`}
                className="p-3 bg-[#0d1d32] border border-[#1e40af]/20 rounded-lg text-gray-400 text-sm text-center hover:border-blue-400 hover:text-blue-400 transition-colors"
              >
                Is {data.name.length > 15 ? data.name.substring(0, 15) + '...' : data.name} Legit?
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScalableEntityPage;
